<template>
  <section class="financial-transfers-toolbar app-filter-toolbar">
    <div
      class="toolbar-main-row app-filter-toolbar-row financial-transfers-toolbar-row"
      :class="{ 'financial-transfers-toolbar-row--open': showFilters }"
    >
      <div class="search-wrap app-filter-search-wrap">
        <i class="fa fa-search" aria-hidden="true"></i>
        <input
          v-model="search"
          type="text"
          :placeholder="labels.searchPlaceholder"
          @input="emitSearch"
          @keyup.enter="emitApply"
        />
      </div>

      <div class="toolbar-right app-filter-toolbar-right">
        <AppFilterDateRange
          v-model="dateRangeValue"
          class="app-filter-date-inline"
          label=""
          :show-input="showDateInput"
          :presets="labels.dateRangePresets"
          initial-preset-key="this_month"
          @change="onDateRangeChange"
        />

        <multiselect
          v-model="sourceAccount"
          :options="sourceOptions"
          track-by="value"
          label="label"
          :multiple="false"
          :close-on-select="true"
          :clear-on-select="false"
          :preserve-search="true"
          :show-labels="false"
          :searchable="true"
          :allow-empty="false"
          :max-height="220"
          :placeholder="labels.sourceAccountLabel"
          class="financial-transfers-account-inline app-filter-select app-select app-select--filters"
        >
          <template #selection="{ values, isOpen }">
            <span v-if="!isOpen" class="multiselect__single">
              {{ selectionLabel(values, labels.sourceAccountLabel) }}
            </span>
          </template>
          <template #noResult>
            <span>{{ labels.noOptions }}</span>
          </template>
          <template #noOptions>
            <span>{{ labels.noOptions }}</span>
          </template>
        </multiselect>

        <button
          type="button"
          class="icon-btn app-filter-icon-btn"
          :class="{ active: showFilters }"
          :title="labels.toggleFilters"
          @click="toggleFilters"
        >
          <i class="fa fa-filter" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="icon-btn app-filter-icon-btn financial-transfers-toolbar-search-btn"
          :title="labels.searchButton"
          @click="emitApply"
        >
          <i class="fa fa-search" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <transition name="filters-expand" @after-leave="handleFiltersAfterLeave">
      <div v-show="showFilters" class="filters-panel app-filter-panel">
        <div class="financial-transfers-filters-grid">
          <div class="filter-field app-filter-field">
            <span>{{ labels.destinationAccountLabel }}</span>
            <multiselect
              v-model="targetAccount"
              :options="targetOptions"
              track-by="value"
              label="label"
              :multiple="false"
              :close-on-select="true"
              :clear-on-select="false"
              :preserve-search="true"
              :show-labels="false"
              :searchable="true"
              :allow-empty="false"
              :max-height="220"
              :placeholder="labels.destinationAccountLabel"
              class="app-filter-select app-select app-select--filters"
            >
              <template #selection="{ values, isOpen }">
                <span v-if="!isOpen" class="multiselect__single">
                  {{ selectionLabel(values, labels.destinationAccountLabel) }}
                </span>
              </template>
              <template #noResult>
                <span>{{ labels.noOptions }}</span>
              </template>
              <template #noOptions>
                <span>{{ labels.noOptions }}</span>
              </template>
            </multiselect>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import AppFilterDateRange from '@/components/common/AppFilterDateRange.vue';
import {
  buildToolbarApplyPayload,
  createToolbarAllOption,
  extractToolbarDateRange,
  resolveToolbarToggleState,
  shouldEmitToolbarDateChange,
} from '@/composables/shared/useFilterToolbarShared';
import { buildCurrentMonthDateRange } from '@/utils/datePresentation';

const normalizeOptions = (items, allLabel) => {
  const allOption = createToolbarAllOption({ default: allLabel }, 'default');
  const rows = Array.isArray(items) ? items : [];

  return [allOption].concat(rows
    .map((item) => {
      const value = Number(item?.value || 0);
      const label = String(item?.label || '').trim();
      if (value <= 0 || label === '') {
        return null;
      }

      return { value, label };
    })
    .filter(Boolean));
};

const findOption = (options, value) => {
  const rows = Array.isArray(options) ? options : [];
  return rows.find((item) => Number(item?.value || 0) === Number(value || 0)) || rows[0] || null;
};

export default {
  name: 'FinancialTransfersToolbar',
  components: {
    AppFilterDateRange,
  },
  props: {
    labels: {
      type: Object,
      required: true,
    },
    accountOptions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['search-change', 'filters-change', 'apply'],
  data() {
    return {
      search: '',
      showFilters: false,
      showDateInput: false,
      dateRangeValue: buildCurrentMonthDateRange(),
      sourceAccountId: 0,
      targetAccountId: 0,
    };
  },
  computed: {
    sourceOptions() {
      return normalizeOptions(this.accountOptions, this.labels.allAccounts);
    },
    targetOptions() {
      return normalizeOptions(this.accountOptions, this.labels.allAccounts);
    },
    sourceAccount: {
      get() {
        return findOption(this.sourceOptions, this.sourceAccountId);
      },
      set(option) {
        this.sourceAccountId = Number(option?.value || 0);
      },
    },
    targetAccount: {
      get() {
        return findOption(this.targetOptions, this.targetAccountId);
      },
      set(option) {
        this.targetAccountId = Number(option?.value || 0);
      },
    },
  },
  methods: {
    selectionLabel(values, fallbackLabel = '') {
      const selected = Array.isArray(values) ? values : [];
      const first = selected[0];
      const label = String(first?.label || '').trim();
      if (label !== '') {
        return label;
      }

      return String(fallbackLabel || '').trim();
    },
    toggleFilters() {
      const nextState = resolveToolbarToggleState(this.showFilters);
      this.showFilters = nextState.showFilters;

      if (nextState.showDateInput !== null) {
        this.showDateInput = nextState.showDateInput;
      }
    },
    handleFiltersAfterLeave() {
      this.showDateInput = false;
    },
    onDateRangeChange(payload) {
      if (!shouldEmitToolbarDateChange(payload, this.dateRangeValue)) {
        return;
      }

      this.emitApply();
    },
    emitSearch() {
      this.$emit('search-change', this.search);
    },
    extractDateRange() {
      return extractToolbarDateRange(this.dateRangeValue, {
        startKey: 'date_from',
        endKey: 'date_to',
      });
    },
    emitFilters() {
      const payload = {
        ...this.extractDateRange(),
        financial_account_source_id: Number(this.sourceAccountId || 0) > 0 ? Number(this.sourceAccountId) : '',
        financial_account_target_id: Number(this.targetAccountId || 0) > 0 ? Number(this.targetAccountId) : '',
      };

      this.$emit('filters-change', payload);
      return payload;
    },
    emitApply() {
      this.emitSearch();
      const filters = this.emitFilters();
      this.$emit('apply', buildToolbarApplyPayload(this.search, filters));
    },
  },
};
</script>

<style scoped>
.financial-transfers-toolbar-row--open {
  align-items: flex-start;
}

.financial-transfers-toolbar-row--open .toolbar-right {
  align-items: flex-start;
}

.financial-transfers-toolbar-row--open .toolbar-right > * {
  align-self: flex-start;
}

.financial-transfers-account-inline {
  --app-filter-inline-width: 238px;
  width: 238px;
  min-width: 238px;
  flex: 0 0 238px;
  margin-top: 4px;
}

.financial-transfers-toolbar-search-btn {
  background-color: #0070cd;
  border-color: #0070cd;
  color: #ffffff;
}

.financial-transfers-toolbar-search-btn i {
  color: #ffffff;
}

.financial-transfers-toolbar-search-btn:hover,
.financial-transfers-toolbar-search-btn:focus {
  background-color: #005eae;
  border-color: #005eae;
}

.financial-transfers-filters-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(1, minmax(0, 1fr));
}
</style>
